import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { ListViewAllModule } from '@syncfusion/ej2-angular-lists';
import { HelperFunctionService } from '../../../../core/services/helper-function.service';
import { ComponentType, Constants, SYSTEM_BUILT_IN_CUSTOM_VIEWS } from '../../../../core/const/constants';
import { NgIf } from '@angular/common';
import { DropDownButtonModule } from '@syncfusion/ej2-angular-splitbuttons';
import {
  UserPersonalizationHelper
} from '@techextensor/tab-core-utility';
import { CustomComponentValueSchema } from '../../../../core/interfaces/common.interface';
import { isEmpty } from 'lodash';
import { firstValueFrom, from } from 'rxjs';
import { PermissionService } from '../../../../core/services/permission.service';
import { TabSdk } from '@techextensor/tab-sdk';
import { UserFavoritesService } from '../../../../core/services/user-favorites.service';
import { TranslationToken } from '../../../../core/const/translation.token';

@Component({
  selector: 'app-tab-customview',
  imports: [ListViewAllModule, NgIf, DropDownButtonModule],
  templateUrl: './tab-customview.component.html',
  styleUrl: './tab-customview.component.scss'
})
export class TabCustomviewComponent {
  public sdk = TabSdk;
  private targetComponentPersonalizedData: any;
  public customViewList: any;
  public selectedCustomView: any;
  public favoriteCount: number = 0;
  public initialized: boolean = false;

  private readonly helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
  private readonly userPersonalizationHelper: UserPersonalizationHelper = inject(UserPersonalizationHelper);
  private readonly permissionService: PermissionService = inject(PermissionService);
  public readonly userFavoritesService: UserFavoritesService = inject(UserFavoritesService);

  @Input() public isInDesigningMode: boolean = false;
  private permissionCache!: boolean;

  public readonly FAVORITE_RECORDS_VIEW = SYSTEM_BUILT_IN_CUSTOM_VIEWS.FAVORITE_RECORDS_VIEW;
  public readonly SYSTEM_RECORDS_VIEW = SYSTEM_BUILT_IN_CUSTOM_VIEWS.SYSTEM_RECORDS_VIEW;
  public readonly APP_RECORDS_VIEW = SYSTEM_BUILT_IN_CUSTOM_VIEWS.APP_RECORDS_VIEW;

  public _value: CustomComponentValueSchema = {
    component: {},
    componentValue: {},
    parameters: {},
    otherFormdata: {}
  };

  @ViewChild('dropdownBtn') dropdownButton!: any;
  @ViewChild('listViewComponent') listViewComponent!: any;

  @Output() onChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly valueChange: EventEmitter<any> = new EventEmitter();

  /**
   * Indicates whether the component is in system view mode.
   *
   * When the component is in system view mode, it will display the system views
   * (e.g. Favorite Records, System Records, App Records) instead of the custom views.
   *
   * @returns {boolean} True if the component is in system view mode.
   */
  get isSystemViewMode(): boolean {
    return this._value.component?.Configuration?.componentValue?.enableSystemViews;
  }

  /**
   * Gets the current views to be displayed in the component.
   *
   * When the component is in system view mode, it will return the system views
   * (e.g. System Records, App Records) instead of the custom views.
   *
   * @returns {any[]} An array of the current views.
   */
  get currentViews(): any[] {
    if (this.isSystemViewMode) {
      // Return the system views when in system view mode.
      return [this.SYSTEM_RECORDS_VIEW, this.APP_RECORDS_VIEW];
    }

    const views = [];
    if (this._value.component?.Configuration?.componentValue?.enableFavoriteRecords) {
      // Add the favorite records view if enabled.
      views.push(this.FAVORITE_RECORDS_VIEW);
    }
    return views;
  }

  /**
   * Gets a boolean indicating whether custom views are available for the component.
   *
   * Custom views are considered available if the component is in system view mode,
   * or if the component is configured to enable favorite records, or if there are
   * any custom views in the custom view list.
   *
   * @returns {boolean} True if custom views are available.
   */
  get customViewsAvailable(): boolean {
    return this._value.component?.Configuration?.componentValue?.enableFavoriteRecords ||
      this.isSystemViewMode ||
      this.customViewList?.length;
  }

  /**
   * Setter for the value property.
   * This method handles the updating of the component's internal state based on the provided value.
   *
   * @param {any} v - The value to set.
   */
  @Input()
  public set value(v: any) {
    // Destructure the provided value object to extract the component, otherFormdata, componentValue, and parameters properties.
    const { component, otherFormdata, componentValue, parameters } = v;

    // Update the component's internal state with the provided values.
    // If a value is not provided, use the existing value.
    this._value.otherFormdata = otherFormdata ?? this._value.otherFormdata;
    this._value.parameters = parameters ?? this._value.parameters;

    // If a component is provided and the component's internal state does not have a component,
    // update the component's internal state with the provided component.
    // Additionally, if the component's Configuration.componentValue.dataSourceQueryId is not empty,
    // load the custom view list.
    if (component && isEmpty(this._value.component)) {
      this._value.component = component;
      if (this._value.component?.Configuration?.componentValue?.dataSourceQueryId) {
        this.loadCustomViewList();
      }
    }

    if (componentValue) {
      this._value.componentValue = componentValue;
      if (isEmpty(this._value.componentValue) && this.selectedCustomView) {
        this._value.componentValue = { ...this.selectedCustomView };
        this.valueChange.emit(this._value);
      }
    }
  }

  /**
   * Returns the value of the component.
   *
   * This method checks if all the properties of the internal value object are empty.
   * If all properties are empty, it returns undefined. Otherwise, it returns the internal value object.
   *
   * @return {any} The value of the component. It can be undefined if all properties are empty.
   */
  public get value(): any {
    // Check if all properties of the internal value object are empty
    if (
      isEmpty(this._value.component) &&
      isEmpty(this._value.componentValue) &&
      isEmpty(this._value.otherFormdata) &&
      isEmpty(this._value.parameters)
    ) {
      // If all properties are empty, return undefined
      return undefined;
    }
    // Return the internal value object
    return this._value;
  }

  /**
   * Loads the custom view list from the DataSourceQuery API using the provided data source query ID.
   *
   * This method sends a request to the DataSourceQuery API with the provided data source query ID
   * and retrieves the custom view list. If the response is valid and contains at least one custom view,
   * it updates the `customViewList` property with the retrieved custom view list and sets the `selectedCustomView`
   * property to the default custom view.
   */
  loadCustomViewList() {
    // If system views are enabled, skip loading custom views and set default
    if (this._value.component?.Configuration?.componentValue?.enableSystemViews) {
      this.getDefaultCustomView(this._value.component?.Configuration?.componentValue);
      return;
    }

    // Send a request to the DataSourceQuery API with the provided data source query ID
    this.helperFunctionService
      .getDSQData(
        Constants.Grid_CustomViewList_Id, // The ID of the DataSourceQuery for custom view list
        { DatasourceQueryID: this._value.component?.Configuration?.componentValue?.dataSourceQueryId }, // The data source query ID
        undefined,
        false,
        TabSdk.util.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode)
      )
      .subscribe((response: any) => {
        // If the response is valid and contains at least one custom view
        if (response && response?.Result?.length > 0 || this._value.component?.Configuration?.componentValue?.enableFavoriteRecords) {
          // Update the custom view list with the retrieved custom view list
          this.customViewList = response.Result;
          // Set the selected custom view to the default custom view
          this.getDefaultCustomView(this._value.component?.Configuration?.componentValue);
        }
      });
  }

  /**
   * Sets the default custom view based on the provided configuration.
   *
   * If the configuration contains a default custom view, it sets the `selectedCustomView` property to the default custom view.
   * If the configuration does not contain a default custom view, it sets the `selectedCustomView` property to the first custom view in the `customViewList`.
   * If the `customViewList` is empty, it sets the `selectedCustomView` property to null.
   * 
   * @param {any} configuration - The configuration object containing the default custom view.
   */
  private getDefaultCustomView(configuration: any) {
    let selectedView = null;

    if (this._value.component?.Configuration?.componentValue?.enableSystemViews) {
      selectedView = this.getDefaultSystemView(configuration);
    } else {
      selectedView = this.getDefaultLegacyView(configuration);
    }

    // Set the selected view if found
    if (selectedView) {
      this.selectedCustomView = selectedView;
      this._value.componentValue = { ...selectedView };
      this.valueChange.emit(this._value);
    }
  }

  /**
   * Returns the default system view based on the provided configuration.
   * 
   * @param {any} configuration - The configuration object containing the default system view.
   * @returns {any} The default system view or null if not found.
   */
  private getDefaultSystemView(configuration: any): any {
    let selectedView = null;

    if (this._value.component?.Configuration?.componentValue?.isUserPersonalizationSupported) {
      const DefaultPersonalizedData: any = this.helperFunctionService.getUserPersonalization(
        configuration?.targetComponentScreen,
        configuration?.targetComponent,
        configuration?.targetComponentType,
        configuration?.targetComponentType === ComponentType.Grid ? configuration?.dataSourceQueryId : ''
      );

      this.targetComponentPersonalizedData = DefaultPersonalizedData[0];

      if (this.targetComponentPersonalizedData?.Config) {
        const config = JSON.parse(this.targetComponentPersonalizedData?.Config);

        if (config?.DefaultCustomViewID === this.SYSTEM_RECORDS_VIEW.Id) {
          selectedView = this.SYSTEM_RECORDS_VIEW;
        } else if (config?.DefaultCustomViewID === this.APP_RECORDS_VIEW.Id) {
          selectedView = this.APP_RECORDS_VIEW;
        }
      }
    }

    // Fallback: Default to App Records if no personalized default found
    return selectedView || this.APP_RECORDS_VIEW;
  }

  /**
   * Returns the default legacy view based on the provided configuration.
   * 
   * @param {any} configuration - The configuration object containing the default legacy view.
   * @returns {any} The default legacy view or null if not found.
   */
  private getDefaultLegacyView(configuration: any): any {
    let selectedView = null;

    if (this._value.component?.Configuration?.componentValue?.isUserPersonalizationSupported) {
      const DefaultPersonalizedData: any = this.helperFunctionService.getUserPersonalization(
        configuration?.targetComponentScreen,
        configuration?.targetComponent,
        configuration?.targetComponentType,
        configuration?.targetComponentType === ComponentType.Grid ? configuration?.dataSourceQueryId : ''
      );

      this.targetComponentPersonalizedData = DefaultPersonalizedData[0];

      if (this.targetComponentPersonalizedData?.Config) {
        const config = JSON.parse(this.targetComponentPersonalizedData?.Config);

        if (config?.DefaultCustomViewID === this.FAVORITE_RECORDS_VIEW.Id) {
          selectedView = this.FAVORITE_RECORDS_VIEW;
        } else {
          selectedView = this.customViewList?.find((customView: any) => customView.Id === config?.DefaultCustomViewID);
        }
      }
    }

    // Fallback: Use system default if no personalized default found
    return selectedView || this.customViewList?.find((customView: any) => customView.IsDefault);
  }

  /**
   * Opens the custom view sidebar for editing or adding a new custom view.
   *
   * @param {any} event - The event object containing the ID of the custom view to edit or undefined for adding a new custom view.
   * @param {string} mode - The mode of the custom view action ('edit' or 'add').
   */
  openCustomView(event: any, mode: string) {
    // Create the payload object for the custom event
    const payload = {
      // The ID of the screen to open in the sidebar
      screenId: Constants.Custom_View_ScreenID,
      // The record object to pass to the screen (contains the ID of the custom view to edit or undefined for adding a new custom view)
      record: mode === 'edit' ? { Id: event.Id } : undefined,
      // The submission object to pass to the screen (contains the data source query ID for adding a new custom view)
      submission:
        mode === 'add'
          ? { DataSourceQueryId: this._value.component?.Configuration?.componentValue?.dataSourceQueryId }
          : undefined,
      // The refresher function to call after the custom view is saved or deleted
      refresher: () => this.loadCustomViewList()
    };

    // Dispatch a custom event to open the custom view sidebar
    window.dispatchEvent(
      // The custom event to dispatch
      new CustomEvent('openTabSidebar', { detail: payload })
    );
  }

  /**
   * Changes the selected custom view and triggers the value change event.
   *
   * @param {any} data - The data object representing the selected custom view.
   */
  changeCustomView(data: any) {
    // Set the selected custom view to the provided data
    this.selectedCustomView = data;

    // Trigger the value change event with the selected custom view data
    this.onValueChange(data);
  }

  /**
   * Handles the select event and emits the new value to the parent component.
   *
   * @param {any} event - The event object containing the selected index.
   */
  onSelect(event: any) {
    // Get the selected index from the event args
    const selectedIndex = event.index;

    // Use the index to get the data from your source array
    const selectedData = this.customViewList[selectedIndex];

    // Trigger the value change event with the selected custom view data
    this.changeCustomView(selectedData);
  }

  /**
   * Marks the provided custom view as the default custom view.
   * If user personalization is supported, it updates the custom view in the Tabd_CustomViews table.
   * If user personalization is not supported, it updates the custom view in the user's personalization data.
   *
   * @param {any} data - The data object representing the custom view to be marked as default.
   * @return {Promise<void>} - A promise that resolves when the custom view is marked as default.
   */
  async markAsDefaultCustomView(data: any) {
    if (this._value.component?.Configuration?.componentValue?.isUserPersonalizationSupported) {
      let userPersonalizeData: any;
      const currentState = this.getCurrentState(data);
      const configuration = this._value.component?.Configuration?.componentValue

      if (!this.targetComponentPersonalizedData) {
        userPersonalizeData = {
          ScreenId: configuration?.targetComponentScreen,
          ComponentId: configuration?.targetComponent,
          Type: configuration?.targetComponentType,
          Config: currentState
        }
      } else {
        userPersonalizeData = {
          ...this.targetComponentPersonalizedData,
          Config: currentState
        };
      }

      this.helperFunctionService.manageUserPersonalization(
        'save',
        userPersonalizeData,
        null,
        configuration?.targetComponentType === ComponentType.Grid ? configuration?.dataSourceQueryId : '',
        TabSdk.util.buildComponentRequestHeaders(this._value.component)
      )?.subscribe((res: any) => {
        if (res) {
          this.targetComponentPersonalizedData = res;
          this.selectedCustomView = data;
          this.onValueChange(data);
        }
      });
    } else {
      let response: any;
      const defaultCustomView = this.customViewList.find((customView: any) => customView.IsDefault);

      if (defaultCustomView?.Id) {
        response = await this.updateCustomView({ IsDefault: false, Id: defaultCustomView.Id });
      }

      if ((response && response?.StatusCode == '200') || !defaultCustomView?.Id) {
        response = await this.updateCustomView({ IsDefault: true, Id: data.Id });
        if (response?.StatusCode == '200') {
          this.loadCustomViewList();
          this.onValueChange(data);
        }
      }
    }
  }

  /**
   * Updates a custom view in the Tabd_CustomViews table.
   *
   * @param {any} data - The data object representing the custom view to be updated.
   * @return {Observable<any>} - An observable that emits the response from the update operation.
   */
  private updateCustomView(data: any) {
    return TabSdk.crud.update(
      {
        appObjectIdOrName: Constants.Tabd_CustomViews_Id,
        data: data,
        criteria: { Id: data.Id }
      },
      {
        skipDefaultToast: true,
        headers: TabSdk.util.buildComponentRequestHeaders(this._value.component)
      }
    );
  }

  private getCurrentState(data: any) {
    const config = JSON.parse(this.targetComponentPersonalizedData?.Config ?? '{}');
    config['DefaultCustomViewID'] = data.Id;
    return JSON.stringify(config);
  }

  private loadUserPersonalizedData() {
    this.helperFunctionService.getDSQData(
      Constants.Get_UserWise_Personalize_Data_Id,
      undefined,
      undefined,
      false,
      TabSdk.util.buildComponentRequestHeaders(this._value.component, this.isInDesigningMode)
    ).subscribe((res: any) => {
      this.userPersonalizationHelper.setUserPersonalizedData(res?.Result ?? []);
    });
  }

  /**
   * Deletes a custom view from the Tabd_CustomViews table.
   *
   * @param {any} data - The data object representing the custom view to be deleted.
   * @return {void} This function does not return anything.
   */
  async deleteCustomView(data: any): Promise<void> {
    const isConfirmed = await TabSdk.ui.confirmDelete();
    if (isConfirmed) {
      await TabSdk.crud.delete(
        {
          appObjectIdOrName: Constants.Tabd_CustomViews_Id,
          criteria: { Id: data.Id }
        },
        {
          skipDefaultToast: true,
          headers: TabSdk.util.buildComponentRequestHeaders(this._value.component)
        }
      );

      await this.deletePersonalizedData(data);
      TabSdk.ui.showSuccess({ message: TranslationToken.CustomViewDeleted });
    }
  }

  private async deletePersonalizedData(data: any): Promise<void> {
    await TabSdk.crud.delete(
      {
        appObjectIdOrName: Constants.TABD_UserPersonalization_Id,
        criteria: { Key: data.Id }
      },
      {
        skipDefaultToast: true,
        headers: TabSdk.util.buildComponentRequestHeaders(this._value.component)
      }
    );
    this.loadCustomViewList();
    this.loadUserPersonalizedData();
  }

  /**
   * Triggers the value change event and the onChange event with the provided data.
   *
   * @param {any} data - The data object representing the new value.
   * @return {void} This function does not return anything.
   */
  private onValueChange(data: any): void {
    // Create a copy of the _value object and assign it to the _value property
    this._value = TabSdk.util.cloneAndUpdateComponentValue(this._value, { ...data });

    // Emit the updated _value object as the value change event
    this.valueChange.emit(this._value);

    // Emit the provided data object as the onChange event
    this.onChange.emit(data);
  }

  public doesHaveAdministratorPermissions(): boolean {
    if (this.permissionCache === undefined) {
      this.permissionCache = (
        TabSdk.store.environmentName === 'Development' &&
        (this.permissionService.doesUserIsTenantAdministrator() || this.permissionService.doesUserIsTabAdministrator())
      );
    }
    return this.permissionCache;
  }

  /**
   * This method is called when the dropdown of custom views is opened.
   * It refreshes the count of favorite records.
   *
   * @return {void} This function does not return anything.
   */
  public onDropdownOpen(): void {
    // Refresh the count of favorite records
    this.refreshFavoriteCount();
  }

  /**
   * This method is called when the dropdown of custom views is opened.
   * It refreshes the count of favorite records.
   *
   * @return {void} This function does not return anything.
   */
  public refreshFavoriteCount(): void {
    // Get the parent app object of the data source query
    const currentAppObject: any = this.helperFunctionService.getCRUDAppObject(undefined, this._value.component.Configuration.componentValue.dataSourceQueryId);

    // Get the count of favorite records for the current app object
    this.favoriteCount = this.userFavoritesService.getFavoriteCount(currentAppObject?.ID);
  }

  /**
   * Generic function to select any built-in view (favorite, system, or app records)
   * @param {any} view - The view object to select
   */
  selectBuiltInView(view: any): void {
    // Clear listview selection if it exists
    this.listViewComponent?.selectItem(null);

    // Set as selected and emit change
    this.selectedCustomView = view;
    this.onValueChange(view);

    // Close dropdown
    if (this.dropdownButton) {
      this.dropdownButton.toggle();
    }
  }
}


// if (this.allowPersonalizeDataInDevelopment()) this.updateUserPersonalizeData(data);
// else this.insertUserPersonalizeData(data);
// private allowPersonalizeDataInDevelopment(): boolean {
//   return (
//     this.targetComponentPersonalizedData &&
//     (this.targetComponentPersonalizedData.UserId === this.user?.Attributes?.Id ||
//       this.permissionService.doesUserIsAppOwner(this.user?.Attributes?.Id))
//   );
// }

// private insertUserPersonalizeData(data: any): void {
//   const currentState = this.getCurrentState(data);
//   const body = {
//     data: {
//       ...this.targetComponentPersonalizedData,
//       UserId: this.user?.Attributes?.Id,
//       Config: currentState
//     }
//   };

//   (this.tabInsertService as any)
//     .insertByObjectId(Constants.TABD_UserPersonalization_Id, body)
//     .subscribe((res: any) => {
//       this.targetComponentPersonalizedData = { ...body.data, Id: res?.Result };
//       this.selectedCustomView = data;
//       this.onValueChange(data);
//       this.loadUserPersonalizedData();
//     });
// }

// updateUserPersonalizeData(data: any) {
//   const currentState = this.getCurrentState(data);
//   const queryFieldsArray = [{ FieldName: 'Id', FieldValue: this.targetComponentPersonalizedData.Id }];
//   const payload = {
//     data: { Config: currentState }
//   };

//   (this.tabUpdateService as any)
//     .updateUsingDSQ(
//       Constants.Get_UserPersonalize_Data_Id,
//       payload,
//       Constants.TABD_UserPersonalization,
//       queryFieldsArray
//     )
//     .subscribe((res: any) => {
//       this.targetComponentPersonalizedData.Config = currentState;
//       this.selectedCustomView = data;
//       this.onValueChange(data);
//       this.loadUserPersonalizedData();
//     });
// }